import type React from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUpdateProject } from '../../hooks/useProjects';
import {
  Button,
  SaveIcon,
  DownloadIcon,
  ShaderPickerModal,
} from '@shadeables/ui';
import { shaderModels } from '../../types';
import type { ShaderName } from '../../types';
import {
  generateExportCode,
  type ExportData,
  type ShaderConfig,
  getShaderPickerData,
  createPreviewShader,
} from '@shadeables/shadermodel';
import { ProjectProvider, useProjectContext } from './ProjectContext';
import { FormSection } from './sections/form';
import { PreviewSection } from './sections/preview';
import { useCurrentPlan } from '@shadeables/billing';
import ExportModal from '../../components/modals/ExportModal';
import styles from './Project.module.css';
import { useCurrentProject } from '../../hooks/useCurrentProject';

// Internal component that uses the context
const ProjectContent: React.FC = () => {
  const { data: project } = useCurrentProject();
  const { shaderModel, selectedShader, setSelectedShader } =
    useProjectContext();
  const updateMutation = useUpdateProject();
  const [isExportModalOpen, setExportModalOpen] = useState(false);
  const [isShaderPickerOpen, setShaderPickerOpen] = useState(false);
  const [exportData, setExportData] = useState<ExportData | null>(null);
  const { canAccess3DShaders } = useCurrentPlan();

  const handleSave = useCallback(async () => {
    if (project && shaderModel && selectedShader) {
      // Convert the shader model instance to config by copying all non-method properties
      const config: ShaderConfig = {
        name: selectedShader,
        ...Object.fromEntries(
          Object.entries(shaderModel).filter(
            ([key, value]) => key !== 'generate' && typeof value !== 'function'
          )
        ),
      } as ShaderConfig;

      updateMutation.mutate({ projectId: project.id, config });
    }
  }, [project, shaderModel, selectedShader, updateMutation]);

  const handleShaderSelect = useCallback(
    (itemId: string) => {
      setSelectedShader(itemId as ShaderName);
    },
    [setSelectedShader]
  );

  const handleExport = useCallback(async () => {
    if (shaderModel) {
      const data = await generateExportCode(shaderModel);
      setExportData(data);
      setExportModalOpen(true);
    }
  }, [shaderModel]);

  // Memoize the shader picker data
  const shaderPickerData = useMemo(() => {
    const data = getShaderPickerData();
    // Mark 3D shaders as disabled if user doesn't have access
    return data.map((category) => ({
      ...category,
      items: category.items.map((item) => {
        const shaderInfo = shaderModels[item.itemId as ShaderName];
        const is3D = shaderInfo.type === '3d';
        return {
          ...item,
          disabled: is3D && !canAccess3DShaders,
        };
      }),
    }));
  }, [canAccess3DShaders]);

  const handleCloseModal = useCallback(() => {
    setExportModalOpen(false);
  }, []);

  // Use both loading states appropriately
  const showShaderSkeletons = !shaderModel || !selectedShader;

  return (
    <div className={styles.container}>
      <div className={styles.settings}>
        <div className={styles.settingsHeader}>
          {showShaderSkeletons ? (
            <div className={styles.skeletonTitle}></div>
          ) : (
            <h1>Project: {project?.name}</h1>
          )}
          <div className={styles.templateSelector}>
            <label htmlFor="shader-select">Shader Template:</label>
            {showShaderSkeletons ? (
              <div className={styles.skeletonSelect}></div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShaderPickerOpen(true)}
                className={styles.shaderPickerButton}
              >
                {selectedShader
                  ? shaderModels[selectedShader].name
                  : 'Choose Shader'}
              </Button>
            )}
          </div>
        </div>

        <div className={styles.settingsContent}>
          <FormSection />
        </div>

        <div className={styles.settingsFooter}>
          {showShaderSkeletons ? (
            <div className={styles.skeletonButtons}>
              <div className={styles.skeletonButton}></div>
              <div className={styles.skeletonButton}></div>
            </div>
          ) : (
            <>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                leftIcon={<SaveIcon />}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleExport}
                variant="primary"
                leftIcon={<DownloadIcon />}
              >
                Export
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.preview}>
        <PreviewSection />
      </div>

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={handleCloseModal}
        exportData={exportData}
      />

      <ShaderPickerModal
        isOpen={isShaderPickerOpen}
        onClose={() => setShaderPickerOpen(false)}
        onSelect={handleShaderSelect}
        items={shaderPickerData}
        title="Choose a Shader"
      />
    </div>
  );
};

// Main Project component that provides context
const Project: React.FC = () => {
  const { pid } = useParams<{ pid: string }>();

  if (!pid) {
    return <div>Project not found</div>;
  }

  return (
    <ProjectProvider>
      <ProjectContent />
    </ProjectProvider>
  );
};

export default Project;
